import { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../store/rootReducers';
import { setLocation } from '../store/slices/LocationSlice';
import { getAddressFromLatLng } from '../Components/getAddressFromLatLng';

/**
 * Hook for AllPlaces: fetches current location and sets it in Redux.
 * isLocationLoading is true until we have location or we've finished trying (so places API waits before calling).
 * Same pattern as useFeedLocation used by Post.
 */
export function useLocationForPlaces() {
  const dispatch = useDispatch();
  const [locationFetchFailed, setLocationFetchFailed] = useState(false);
  const userLocation = useSelector((state: RootState) => state.location?.location);
  const fetchAttempted = useRef(false);

  // True when we don't have location yet and haven't failed → places feed should wait
  const isLocationLoading = !userLocation && !locationFetchFailed;

  useEffect(() => {
    if (userLocation || !navigator.geolocation) {
      if (!userLocation && !navigator.geolocation) {
        setLocationFetchFailed(true);
      }
      return;
    }
    if (fetchAttempted.current) return;
    fetchAttempted.current = true;

    const fetchLocationIfPermitted = async () => {
      if ('permissions' in navigator) {
        try {
          const permissionStatus = await navigator.permissions.query({
            name: 'geolocation' as PermissionName,
          });
          if (permissionStatus.state === 'granted') {
            await getLocation();
          } else {
            setLocationFetchFailed(true);
          }
        } catch {
          await getLocation().catch(() => setLocationFetchFailed(true));
        }
      } else {
        await getLocation().catch(() => setLocationFetchFailed(true));
      }
    };

    const getLocation = (): Promise<void> =>
      new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          async (position: GeolocationPosition) => {
            const { latitude, longitude } = position.coords;
            try {
              const address = await getAddressFromLatLng(latitude, longitude);
              dispatch(
                setLocation({
                  lat: latitude,
                  lng: longitude,
                  name: address,
                })
              );
              resolve();
            } catch (error) {
              console.error('Error getting address from location:', error);
              dispatch(
                setLocation({
                  lat: latitude,
                  lng: longitude,
                  name: `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`,
                })
              );
              resolve();
            }
          },
          (error: GeolocationPositionError) => {
            console.error('Error getting current location:', error);
            fetchAttempted.current = false;
            setLocationFetchFailed(true);
            reject(error);
          },
          { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
        );
      });

    fetchLocationIfPermitted();
  }, [userLocation, dispatch]);

  return { userLocation, isLocationLoading };
}
